/**
 * Google Analytics 4, loaded only where it belongs.
 *
 * Two guards, both deliberate:
 *
 *   1. No measurement id → every function here is a no-op. Local dev, CI and any environment that
 *      hasn't been given an id send nothing, so developers never pollute the property with their
 *      own clicks and no build depends on the variable existing.
 *
 *   2. Root domain only. Tenant subdomains (slug.gredus.com) are the product, and their URLs carry
 *      student numbers, exam ids and attempt ids — shipping those to Google would be a privacy
 *      problem and is against GA4's terms on personal data. Nothing is lost: the acquisition funnel
 *      worth measuring (landing → /register → /register/confirm) all lives on the root domain.
 *
 * The measurement id is not a secret — it ships inside the page source of every GA site — so it
 * lives in .env alongside the API URL rather than in a vault.
 */
import { isRootDomain } from "./host";

const MEASUREMENT_ID = "G-VDMLGPKYZB"

/**
 * Query parameters worth forwarding: campaign attribution, and nothing else. A whitelist rather
 * than a blocklist, so a URL that one day carries ?email= or ?token= cannot leak it to Google by
 * default — the reason page_location is rebuilt here instead of passing window.location.href.
 */
const ATTRIBUTION_PARAM = /^(utm_[a-z_]+|gclid|fbclid|msclkid|ref)$/i;

let started = false;

export function analyticsEnabled() {
  return Boolean(MEASUREMENT_ID) && typeof window !== "undefined" && isRootDomain();
}

/** origin + path + only the campaign parameters. */
export function analyticsUrl(pathname, search) {
  const kept = new URLSearchParams();
  new URLSearchParams(search || "").forEach((value, key) => {
    if (ATTRIBUTION_PARAM.test(key)) kept.append(key, value);
  });
  const query = kept.toString();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}${pathname}${query ? `?${query}` : ""}`;
}

/** Idempotent: React 18+ mounts effects twice in StrictMode, and a second gtag.js would double-count. */
export function initAnalytics() {
  if (started || !analyticsEnabled()) return;
  started = true;

  window.dataLayer = window.dataLayer || [];
  // Must push the Arguments object itself — gtag.js reads it positionally, so a rest-args arrow
  // function silently produces events Google discards.
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", MEASUREMENT_ID, {
    // Route changes are client-side. Left on, gtag would count the first URL twice (once itself,
    // once from RouteAnalytics) and never see any later route at all.
    send_page_view: false,
    // Traffic and funnel numbers only — no remarketing profiles built from this site's visitors.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  document.head.appendChild(script);
}

export function trackPageView(pathname, search) {
  if (!analyticsEnabled() || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: pathname,
    page_location: analyticsUrl(pathname, search),
  });
}

export function trackEvent(name, params = {}) {
  if (!analyticsEnabled() || !window.gtag) return;
  window.gtag("event", name, params);
}

/**
 * Send an event, then run `done` — for events that immediately precede a navigation away, where a
 * plain gtag call can be cancelled mid-flight.
 *
 * `done` always runs, exactly once, even when gtag.js was blocked by an ad blocker or never
 * loaded. Analytics must never be able to strand a user on the page it was measuring.
 */
export function trackEventThen(name, params, done) {
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    done();
  };
  if (!analyticsEnabled() || !window.gtag) {
    finish();
    return;
  }
  window.gtag("event", name, { ...params, event_callback: finish, event_timeout: 700 });
  // event_timeout only fires if gtag.js is alive to fire it; this is the unconditional backstop.
  setTimeout(finish, 900);
}
